import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled, css } from '@mui/material/styles';
import React from 'react';

import ReportTaskCommentDrawer from './report-task-comment-drawer';

import Button from '~/client/components/base/button';
import { getDesignTokens } from '~/client/components/base/core';
import {
  GenericTableNew,
  TableColumnNew as TableColumn,
} from '~/client/components/base/generic-table';
import { ArrowRight } from '~/client/components/base/icon';
import StatusPill, {
  type StatusTone,
} from '~/client/components/base/status-pill';
import Text from '~/client/components/base/text';
import * as GQL from '~/graphql/generated/client';

interface ReportTaskTableViewProps {
  tasks: GQL.ReportTask[];
  className?: string;
  /** Opens the report's version history, where the decision is actually made. */
  onOpenReport: (task: GQL.ReportTask) => void;
}

/**
 * Where the report stands.
 *
 * The same words the report page uses, so a row and the page it opens agree.
 * An open row says which of the two steps it is waiting on — the row is the
 * reviewer's while in review and the approver's while waiting for approval,
 * and each should recognise their own.
 */
const describeTask = (
  task: GQL.ReportTask
): { label: string; tone: StatusTone } => {
  switch (task.status) {
    case GQL.AssignmentStatusType.Completed:
      return { label: 'Approved', tone: 'success' };
    case GQL.AssignmentStatusType.Rejected:
      return { label: 'Rejected', tone: 'danger' };
    default:
      return task.reportStatus === GQL.ReportVersionStatus.WaitingForApproval
        ? { label: 'Waiting for approval', tone: 'info' }
        : { label: 'In review', tone: 'info' };
  }
};

/**
 * How long is left, or how far past.
 *
 * Overdue is the signal a review backlog lives or dies by, so it is stated
 * rather than left for the reader to work out from a date.
 */
const describeDue = (
  dueDate?: number | null
): { text: string; overdue: boolean } => {
  if (!dueDate) {
    return { text: '—', overdue: false };
  }
  const days = Math.ceil((Number(dueDate) - Date.now()) / 86400000);
  if (days < 0) {
    const late = Math.abs(days);
    return {
      text: `${late} day${late === 1 ? '' : 's'} overdue`,
      overdue: true,
    };
  }
  if (days === 0) {
    return { text: 'Due today', overdue: false };
  }
  return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, overdue: false };
};

const ReportTaskTableViewImpl: React.FC<ReportTaskTableViewProps> = ({
  tasks,
  className,
  onOpenReport,
}) => {
  // Kept here rather than lifted to the page: the drawer belongs to this table
  // and nothing outside it needs to know a comment is open. Mirrors how
  // TaskTableView owns its own comment drawer.
  const [commentTask, setCommentTask] = React.useState<GQL.ReportTask | null>(
    null
  );

  const columns: TableColumn<GQL.ReportTask>[] = [
    {
      key: 'templateName',
      label: (
        <Text sz="body14" color="secondary">
          Report
        </Text>
      ),
      width: '22%',
      render: (_, task) => (
        <Box>
          <Text sz="body14">{task.templateName}</Text>
          <Text sz="body12" color="secondary">
            {`Version ${task.version}`}
          </Text>
        </Box>
      ),
      cellClassName: 'table-cell table-cell-w-22',
    },
    {
      key: 'projectName',
      label: (
        <Text sz="body14" color="secondary">
          Project
        </Text>
      ),
      width: '16%',
      render: (_, task) => (
        <Box>
          <Text sz="body14">{task.projectName}</Text>
          <Text sz="body12" color="secondary">
            {task.projectTypeLabel}
          </Text>
        </Box>
      ),
      cellClassName: 'table-cell table-cell-w-16',
    },
    {
      key: 'assignedToName',
      label: (
        <Text sz="body14" color="secondary">
          Assigned to
        </Text>
      ),
      width: '14%',
      render: (_, task) =>
        task.assignedToName ? (
          <Text sz="body14">{task.assignedToName}</Text>
        ) : (
          // Versions submitted before routing existed have no assignee. Saying
          // so beats an empty cell the reader has to interpret.
          <Text sz="body14" color="secondary">
            Unassigned
          </Text>
        ),
      cellClassName: 'table-cell table-cell-w-14',
    },
    {
      key: 'status',
      label: (
        <Text sz="body14" color="secondary">
          Status
        </Text>
      ),
      width: '20%',
      render: (_, task) => {
        const { label, tone } = describeTask(task);
        // The rejection comment used to be quoted here too. It now lives only
        // in the Comments drawer, matching the assessment task table: the same
        // text in two cells of one row is noise, and the badge already says a
        // comment exists.
        return <StatusPill tone={tone} label={label} />;
      },
      cellClassName: 'table-cell table-cell-w-20',
    },
    {
      key: 'assessmentCount',
      label: (
        <Text sz="body14" color="secondary">
          Assessments
        </Text>
      ),
      width: '16%',
      render: (_, task) =>
        task.assessmentCount === 0 ? (
          <Text sz="body14" color="secondary">
            None covered
          </Text>
        ) : (
          <Box>
            <Text sz="body14">{`${task.assessmentCount} covered`}</Text>
            {task.unapprovedAssessmentCount > 0 && (
              // The single most decision-relevant fact, on the row rather than
              // two clicks away: approving accepts the report as it stands.
              <Text sz="body12" color="secondary">
                {`${task.unapprovedAssessmentCount} not approved`}
              </Text>
            )}
          </Box>
        ),
      cellClassName: 'table-cell table-cell-w-16',
    },
    {
      key: 'dueDate',
      label: (
        <Text sz="body14" color="secondary">
          Due
        </Text>
      ),
      width: '12%',
      render: (_, task) => {
        if (task.status !== GQL.AssignmentStatusType.Pending) {
          return (
            <Text sz="body14" color="secondary">
              {task.decidedByName ?? '—'}
            </Text>
          );
        }
        const due = describeDue(task.dueDate);
        return (
          <Box>
            <Text sz="body14" className={due.overdue ? 'overdue' : undefined}>
              {due.text}
            </Text>
            {/* Who is waiting on you — an approver's first question. */}
            {task.submittedByName && (
              <Text sz="body12" color="secondary">
                {`from ${task.submittedByName}`}
              </Text>
            )}
          </Box>
        );
      },
      cellClassName: 'table-cell table-cell-w-12',
    },
    {
      key: 'comments',
      align: 'center',
      label: (
        <Text sz="body14" color="secondary">
          Comments
        </Text>
      ),
      width: '8%',
      render: (_, task) => (
        <IconButton
          size="small"
          className="comment-btn"
          onClick={(e) => {
            // The row itself navigates to the report, so the comment button has
            // to stop the click reaching it.
            e.stopPropagation();
            setCommentTask(task);
          }}
        >
          <Badge
            color="primary"
            variant="dot"
            // At most two comments — one per step, written at the moment of
            // decision — so a dot saying "there is one" beats a count.
            invisible={!task.review?.comment && !task.approval?.comment}
          >
            <ChatBubbleOutlineIcon fontSize="small" />
          </Badge>
        </IconButton>
      ),
      cellClassName: 'table-cell table-cell-w-8',
    },
    {
      key: 'action',
      label: '',
      width: '4%',
      align: 'right',
      render: () => <Button icon={ArrowRight} />,
      cellClassName: 'table-cell table-cell-w-4',
    },
  ];

  return (
    <div className={className}>
      <GenericTableNew
        columns={columns}
        data={tasks}
        getRowKey={(task) => task.taskId}
        onRowClick={onOpenReport}
        rowClassName="table-row"
        className="table"
        usePaper={false}
        containerClassName=""
        showColumnBorders={false}
        showRowBorders
        headerBackground="light"
      />
      <ReportTaskCommentDrawer
        open={!!commentTask}
        onClose={() => setCommentTask(null)}
        task={commentTask}
      />
    </div>
  );
};

const ReportTaskTableView = styled(ReportTaskTableViewImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    .queue-note {
      display: block;
      margin-top: 4px;
    }

    .overdue {
      color: ${t.status.danger};
      font-weight: 600;
    }

    .table {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      margin: 0;
      border-spacing: 0;
    }

    ${theme.breakpoints.down('md')} {
      min-width: 1000px;
    }

    ${theme.breakpoints.up('md')} {
      min-width: auto;
    }

    .table-cell {
      padding: ${theme.spacing(3)};
      white-space: normal;
      word-wrap: break-word;
      vertical-align: middle;
    }

    .table-cell-w-22 {
      width: 22%;
      padding-left: ${theme.spacing(3)};
    }
    .table-cell-w-16 {
      width: 16%;
    }
    .table-cell-w-14 {
      width: 14%;
    }
    .table-cell-w-20 {
      width: 20%;
    }
    .table-cell-w-16 {
      width: 16%;
    }
    .table-cell-w-12 {
      width: 12%;
    }
    .table-cell-w-8 {
      width: 8%;
    }
    .table-cell-w-4 {
      width: 4%;
    }

    .comment-btn {
      color: ${t.textSecondary};
    }
    .table-row {
      cursor: pointer;
      &:hover {
        background-color: ${t.surface2};
      }
    }
  `;
});

export default ReportTaskTableView;
